// tslint:disable:object-literal-sort-keys max-classes-per-file

/**
 * @file
 * Table with nested rows for expandable data within BasstrakkLeaderboard
 */

import * as React from 'react';
// Components
import Time from '../../../components/Time/Time';
// Types
import {
  AnglerId,
  IBasstrakkAngler,
  ICutline,
  IHighlighted,
} from '../../../types';
// Util
import { checkForCutline } from '../../../util/util';
import { basstrakkCutlineRow } from '../processing';
import { ISponsors } from '../types';

// Constants
const COL_SORT_PARAM = 'key';
const DEFAULT_COL_SPAN = 8;

/**
 * Static header that does not change
 */
interface IHeaderProps {
  sortFunc: (sortCol: BTAKeys) => (e: React.MouseEvent) => void;
}
class Header extends React.PureComponent<IHeaderProps> {
  public render() {
    return (
      <thead>
        <tr>
          <th className="rank" onClick={this.props.sortFunc('Rank')}>
            Rank
          </th>
          <th className="name">Name</th>
          <th className="total">
            <span>Total</span>
            <br />
            <span>Count / Weight / Back</span>
          </th>
          <th className="livewell">
            <a
              href="?key=LimitPdsOz"
              onClick={this.props.sortFunc('BTLimitOunces')}
            >
              Livewell
            </a>
            <br />
            <span>Count / Weight</span>
          </th>
          <th className="updated">Updated</th>
        </tr>
      </thead>
    );
  }
}

/**
 * Is provided all the processed results from the API
 *
 * Is given results from ApiTable, therefore has an implied `data` prop, which
 * we extend by adding `active`
 */
interface ITableProps {
  data: IBasstrakkAngler[];
  active: AnglerId;
  setActive: (active: AnglerId) => void;
  // Optional sponsors list for the given tournament.
  sponsors: ISponsors;
  rows: any[];
  cutlines: ICutline[];
}
interface ITableState {
  sortCol: BTAKeys;
  pollsLeft: number;
}

// Signal that just the keys we'll sort by are safe to do math upon
type BTAKeys = keyof Pick<IBasstrakkAngler, 'Rank' | 'BTLimitOunces'>;
// Runtime of the safe filters we support
const btaFilters = ['Rank', 'BTLimitOunces', 'BTKeptOz', 'BTCullOZ'];

class NormalTable extends React.PureComponent<ITableProps, ITableState> {
  public static defaultProps = {
    data: [],
    sponsors: [],
    cutlines: [],
  };

  public state: ITableState = {
    sortCol: 'Rank',
    pollsLeft: 0,
  };

  // Avoid creating a new arrow function by using a function that takes a param
  // and returns a function ready to take MouseEvent (though it is ignored)
  public setActive = (activeId: AnglerId) => () =>
    this.props.setActive(activeId);

  // Sorter function for headers
  public sort = (a: IBasstrakkAngler, b: IBasstrakkAngler): number => {
    const { sortCol } = this.state;
    // Only 'Rank' sorts ascending
    return sortCol === 'Rank'
      ? a[sortCol] - b[sortCol]
      : b[sortCol] - a[sortCol];
  };

  // Sort columns
  public setSort = (sortCol: BTAKeys): void =>
    this.setState({
      sortCol,
    });

  // Sort by click
  public clickSetSort = (sortCol: BTAKeys) => (e: React.MouseEvent) => {
    e.preventDefault();
    window.history.pushState(
      {},
      '',
      `${window.location.pathname}?${COL_SORT_PARAM}=${sortCol}`
    );
    this.setSort(sortCol);
  };

  // Change sort based on ?key='Rank' | 'BTLimitOunces' | 'BTKeptOz' | 'BTCullOZ'
  public urlToState = () => {
    const key =
      new URLSearchParams(window.location.search).get(COL_SORT_PARAM) || 'Rank';
    if (btaFilters.includes(key)) {
      this.setSort(key as BTAKeys);
    }
  };

  // Checks if the row is highlighted and has an array of sponsor logos.
  public hasSponsors = ({ PollsLeft, sponsorLogos }: IHighlighted) =>
    PollsLeft > 0 && sponsorLogos && sponsorLogos.length;

  // Set sort based on params
  public componentDidMount(): void {
    this.urlToState();
    // Listen to back event, also sort
    window.onpopstate = () => this.urlToState();
  }

  public render() {
    const { active, rows, cutlines } = this.props;
    // Angler rows.

    const renderName = (row: IBasstrakkAngler) => {
      const { FISHERMAN_PHONENAME, PartnerFontname, FISHERMENID, PartnerFishermenId } = row;
      let name1 = FISHERMAN_PHONENAME;
      let name2 = PartnerFontname || '';
      let id1 = FISHERMENID;
      let id2 = PartnerFishermenId || null;

      if (id2 !== null && !name2 && FISHERMAN_PHONENAME.indexOf(' & ') !== -1) {
        const parts = FISHERMAN_PHONENAME.split(' & ');
        name1 = parts[0];
        name2 = parts[1];
      }

      return (
        <>
          <a href={`/angler/profile/${id1}`}>
            {name1}
          </a>
          {name2 ? (
            <>
              <span style={{ fontWeight: 600 }}> & </span>
              <a href={`/angler/profile/${id2}`}>
                {name2}
              </a>
            </>
          ) : null}
        </>
      );
    }

    const rowResults = rows.sort(this.sort).map(row => (
      <tr
        key={row.FISHERMENID}
        onClick={this.setActive(row.FISHERMENID)}
        className={row.PollsLeft && row.PollsLeft > 0 ? 'highlight' : ''}
        data-testid={
          row.PollsLeft && row.PollsLeft > 0 ? 'btlb-highlighted' : ''
        }
      >
        <td colSpan={5}>
          <table>
            <tbody>
              <tr className={active === row.FISHERMENID ? 'btlb-active' : ''}>
                <td className="rank">{row.Rank}</td>
                <td className="name">
                  {renderName(row)}
                  {row.teamname ? (
                    <span className="team-name">{row.teamname}</span>
                  ) : null}
                </td>
                <td className="total">
                  <span>{row.SumFishCount || 0}</span>
                  <span> / </span>
                  <span>{row.SumPdsOz || 0}</span>
                  <span> / </span>
                  <span>{row.BackPdsOz || 0}</span>
                </td>
                <td className="livewell">
                  <span>{row.BTFishLivewell || 0}</span>
                  <span> / </span>
                  <span>{row.BTLimitPdsOz || 0}</span>
                </td>
                <td className="updated">
                  {row.LastLocationTime && <Time time={row.LastLocationTime} />}
                </td>
              </tr>
              {this.hasSponsors(row) ? (
                <tr className="sponsor highlight" data-testid="sponsor">
                  <td colSpan={DEFAULT_COL_SPAN}>
                    {row.sponsorLogos.map((logoUrl: string) => (
                      <img
                        className="sponsor__logo"
                        data-testid="sponsor-logo"
                        src={logoUrl}
                        alt="Basstrakk Sponsor Logo"
                        key={logoUrl}
                      />
                    ))}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </td>
      </tr>
    ));

    return (
      <table className="smart-table">
        <Header sortFunc={this.clickSetSort} />
        <tbody>
          {checkForCutline(rowResults, basstrakkCutlineRow, cutlines)}
        </tbody>
      </table>
    );
  }
}
export default NormalTable;
